import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../components/dialogs/Modal';
import Input from '../../../../common/components/Input';

// review:accepted: every non-digit is dropped rather than rejected, so text around the digits ("send6214now") also confirms. Not a defect: the digits typed still have to be exactly the count shown, which is the friction this dialog is for, and stripping is what lets "6,214" and "6 214" through.
/**
 * Does what the operator typed confirm this count?
 *
 * Digits only, on both sides. The dialog shows the number with
 * `toLocaleString()` - "6,214" - so an operator who types exactly what is on
 * screen must be accepted; comparing against `String(count)` rejected them for
 * every cohort of 1,000 or more, which is precisely the size where the
 * confirmation matters. Stripping separators also accepts "6 214" and a pasted
 * value with stray whitespace, none of which indicate a different intent.
 *
 * Exported so the rule can be tested without rendering the dialog.
 *
 * @param {string} typed
 * @param {number} count
 * @returns {boolean}
 */
export const matchesCount = (typed, count) => {
  const digitsOnly = String(typed).replace(/[^0-9]/g, '');
  // An empty box must never confirm, and neither must a count of zero - there
  // is nothing to send, so the dialog should not be openable in that state.
  if (!digitsOnly || !count) {
    return false;
  }
  return digitsOnly === String(count);
};

/**
 * Confirmation for the bulk manual reminder.
 *
 * Deliberately heavier than an OK button. The two resync actions on this menu
 * move no messages and are safe to re-run, so an operator arrives here trained
 * to click through. This one sends real SMS/email to thousands of patients and
 * cannot be undone, so it asks for the number to be typed back: enough friction
 * that it cannot be dismissed by reflex, and it forces the count to actually be
 * read rather than glanced at.
 */
const ConfirmResendNotifications = ({
  visible,
  count,
  patients,
  onCancel,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');

  // Clear between openings, otherwise a previously typed number would still be
  // sitting there and confirm a different, larger cohort on the next open.
  useEffect(() => {
    if (visible) {
      setTyped('');
    }
  }, [visible]);

  const matches = matchesCount(typed, count);
  const handleOk = useCallback(() => {
    if (matches) {
      onConfirm();
    }
  }, [matches, onConfirm]);

  return (
    <Modal
      data-testid="confirm-resend-notifications"
      visible={visible}
      title={t('confirmations:patientSync.resendConfirm.title')}
      okText={t('confirmations:patientSync.resendConfirm.title')}
      onCancel={onCancel}
      onOk={handleOk}
      // isOkDisabled, not okButtonProps: Modal/ModalAntd renders its own footer
      // buttons and never forwards okButtonProps, so that prop was silently
      // ignored and "Send reminders" stayed enabled however the box was filled
      // in - a confirmation dialog that confirmed nothing.
      isOkDisabled={!matches}
    >
      <div className="stack-4">
        <div>
          {/* "Up to": the count is of candidate activities. At send time each
              activity also has to have a reminder that is due and that the
              project's reminder rules apply to it, so fewer may go out. */}
          <div>{t('confirmations:patientSync.resendConfirm.intro')}</div>
          {/* Label and number on separate rows rather than inside a sentence,
              so no language has to fit a count and its plural into a phrase. */}
          <div>
            {t('confirmations:patientSync.resendConfirm.activities')}:{' '}
            <strong>{count.toLocaleString()}</strong>
          </div>
          <div>
            {t('confirmations:patientSync.resendConfirm.patients')}:{' '}
            <strong>{patients.toLocaleString()}</strong>
          </div>
        </div>
        <div>{t('confirmations:patientSync.resendConfirm.skipped')}</div>
        <div>
          {/* Stated explicitly rather than implied by a single number: one
              activity sends one message per notification group per destination,
              so a patient with both an SMS and an email destination receives
              two. Presenting the activity count as a message count would
              understate what is about to happen. */}
          {t('confirmations:patientSync.resendConfirm.warning')}
        </div>
        <div>
          {t('confirmations:patientSync.resendConfirm.typeToConfirm')}{' '}
          <strong>{count.toLocaleString()}</strong>
        </div>
        <Input
          data-testid="confirm-resend-count"
          value={typed}
          // Input forwards onChange straight to the DOM node, so this receives
          // the event rather than the value.
          onChange={(event) => setTyped(event.target.value)}
          placeholder={String(count)}
        />
      </div>
    </Modal>
  );
};

ConfirmResendNotifications.propTypes = {
  visible: PropTypes.bool,
  count: PropTypes.number,
  patients: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ConfirmResendNotifications.defaultProps = {
  visible: false,
  count: 0,
  patients: 0,
};

export default ConfirmResendNotifications;
